import { useState, useEffect, useCallback } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";
import { IoClose } from "react-icons/io5";
import { HiThumbUp, HiThumbDown } from "react-icons/hi";

import { analytics } from "MVVM/Analytics";

const SURVEY_STORAGE_KEY = "hltv_survey_completed";

export interface HLTVSurveyProps {
  platform: string;
  playlist: string;
}

export const HLTVSurvey = ({ platform, playlist }: HLTVSurveyProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    const hasCompleted = localStorage.getItem(SURVEY_STORAGE_KEY);

    if (!hasCompleted) {
      setIsVisible(true);
      analytics.track("HLTV Survey - Shown", {
        platform,
        playlist,
      });
    }
  }, [platform, playlist]);

  const handleResponse = useCallback(
    (sentiment: "good" | "bad" | "neutral") => {
      setIsAnimatingOut(true);
      analytics.track("HLTV Survey - Response", {
        sentiment,
      });

      setTimeout(() => {
        localStorage.setItem(SURVEY_STORAGE_KEY, sentiment);
        setIsVisible(false);
      }, 200);
    },
    [],
  );

  if (!isVisible) return null;

  return (
    <SurveyOverlay $isAnimatingOut={isAnimatingOut}>
      <CloseButton onClick={() => handleResponse("neutral")}>
        <IoClose size={14} />
      </CloseButton>
      <SurveyContent>
        <SurveyTitle>Enjoying Allstar Highlights?</SurveyTitle>
        <ButtonRow>
          <ResponseButton onClick={() => handleResponse("good")} $isPositive>
            <HiThumbUp size={16} />
            Yes
          </ResponseButton>
          <ResponseButton onClick={() => handleResponse("bad")}>
            <HiThumbDown size={16} />
            No
          </ResponseButton>
        </ButtonRow>
      </SurveyContent>
    </SurveyOverlay>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const SurveyOverlay = styled(Box)<{ $isAnimatingOut: boolean }>`
  animation: ${({ $isAnimatingOut }) => ($isAnimatingOut ? fadeOut : fadeIn)}
    0.2s ease-out forwards;
  align-items: center;
  backdrop-filter: blur(8px);
  background: linear-gradient(
    135deg,
    rgba(28, 42, 52, 0.92) 0%,
    rgba(44, 62, 80, 0.88) 100%
  );

  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
`;

const SurveyContent = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 380px) {
    gap: 8px;
  }
`;

const SurveyTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;

  @media (max-width: 515px) {
    font-size: 13px;
  }

  @media (max-width: 380px) {
    font-size: 12px;
  }
`;

const ButtonRow = styled(Flex)`
  align-items: center;
  gap: 8px;

  @media (max-width: 380px) {
    gap: 6px;
  }
`;

const ResponseButton = styled.button<{ $isPositive?: boolean }>`
  align-items: center;
  background: ${({ $isPositive }) =>
    $isPositive
      ? "linear-gradient(135deg, rgba(77, 189, 148, 0.7) 0%, rgba(49, 113, 138, 0.7) 100%)"
      : "rgba(255, 255, 255, 0.12)"};
  border: 1px solid
    ${({ $isPositive }) =>
      $isPositive ? "rgba(77, 189, 148, 0.5)" : "rgba(255, 255, 255, 0.2)"};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 5px;
  padding: 6px 12px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $isPositive }) =>
      $isPositive
        ? "linear-gradient(135deg, rgba(77, 189, 148, 0.9) 0%, rgba(49, 113, 138, 0.9) 100%)"
        : "rgba(255, 255, 255, 0.2)"};
  }

  @media (max-width: 515px) {
    font-size: 11px;
    padding: 5px 10px;
  }

  @media (max-width: 380px) {
    font-size: 10px;
    gap: 4px;
    padding: 4px 8px;

    svg {
      height: 12px;
      width: 12px;
    }
  }
`;

const CloseButton = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  height: 22px;
  justify-content: center;
  position: absolute;
  right: 6px;
  top: 6px;
  transition: all 0.15s ease;
  width: 22px;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
    color: ${({ theme }) => theme.colors.ice};
  }

  @media (max-width: 380px) {
    height: 18px;
    right: 4px;
    top: 4px;
    width: 18px;

    svg {
      height: 12px;
      width: 12px;
    }
  }
`;

export default HLTVSurvey;
